import React, { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, FolderOpen, Users, Lock, LogOut, Mail, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import WishlinkLogo from "../components/WishlinkLogo";

const NAV = [
  { to: "/admin/campaigns", label: "Campaigns", icon: LayoutGrid, testid: "nav-campaigns" },
  { to: "/admin/brands", label: "Brands & Teams", icon: FolderOpen, testid: "nav-brands" },
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-users" },
  { to: "/admin/whitelist", label: "Admin Whitelist", icon: Lock, testid: "nav-whitelist" },
  { to: "/admin/emails", label: "Email management", icon: Mail, testid: "nav-emails" },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [counts, setCounts] = useState({ brands: 0, teams: 0 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/brands");
        const teams = data.brands.reduce((a, b) => a + (b.team_count || 0), 0);
        setCounts({ brands: data.brands.length, teams });
      } catch (e) {
        console.error("[AdminLayout] failed to load brands count", e);
      }
    })();
  }, []);

  const handleLogout = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-page)" }}>
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-[var(--border-default)] flex flex-col p-5" style={{ background: "linear-gradient(180deg, var(--bg-tint) 0%, var(--bg-page) 30%)" }} data-testid="admin-sidebar">
        <Link to="/admin/settings" className="mb-10 mt-2 flex flex-col items-center justify-center group" data-testid="sidebar-branding-link" title="Click to change Wishlink logo">
          <WishlinkLogo
            className="h-9 w-auto max-w-[160px] object-contain group-hover:opacity-80 transition"
            fallbackClassName="font-display text-4xl font-semibold leading-tight tracking-tight text-[var(--brand)]"
          />
          <span className="mt-1.5 text-[10px] font-semibold tracking-[0.22em] uppercase text-[var(--brand)]" data-testid="kiwi-wordmark">Kiwi</span>
        </Link>

        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to} to={to} data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[var(--brand)]"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white"
                }`
              }
              style={({ isActive }) => isActive ? { background: "var(--brand-tint)" } : {}}
            >
              <Icon size={18} strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-6 border-t border-[var(--border-default)]">
          <div className="text-xs text-[var(--text-secondary)] mb-2">Signed in as</div>
          <div className="text-sm font-medium truncate" data-testid="signed-in-email">{user?.email}</div>
          <div className="text-xs text-[var(--text-secondary)] capitalize">{user?.role || "admin"}</div>
          <button onClick={handleLogout} className="wl-btn-ghost mt-3 -ml-2 inline-flex items-center gap-2" data-testid="sign-out-btn">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="px-10 pt-6 pb-2 text-xs text-[var(--text-secondary)] tracking-wide" data-testid="admin-breadcrumb">
          Wishlink admin · {counts.brands} brand{counts.brands === 1 ? "" : "s"} · {counts.teams} team{counts.teams === 1 ? "" : "s"}
        </div>
        <div className="px-10 pb-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
